<template>
  <div data-test-id="ListFormComponent">
    <Spinner v-if="loading" />
    <h1>{{ listTitle }}</h1>
    <section v-if="!loading && showCsv" class="top-right" title="Download table contents as CSV." @click="onBtExport()">
      <button class="btn btn btn-outline-secondary btn-sm" style="font-size: 0.8rem; margin-top: 10px;">
        Download CSV <b-icon-download></b-icon-download>
      </button>
    </section>
    <div class="list-form-total-line mt-4 mb-2">
      <span>{{ filteredRowCount }}&nbsp;{{ pluralName }}</span>
      <span>&nbsp;&nbsp;{{ countMessage }}</span>
    </div>
    <div v-if="refreshType" class="list-form-refresh">
      <b-button title="Refresh" variant="outline-danger" @click="onRefresh">
        <b-icon-arrow-clockwise></b-icon-arrow-clockwise>
        <span>Update Table</span>
      </b-button>
    </div>
    <div v-if="showModalButton === 'true'" class="text-right">
      <b-button
        v-if="showModalButton === 'true'"
        data-test-id="OpenModalButton"
        variant="outline-danger"
        @click="showModal"
      >
        <span>{{ modeTitle }}</span>
      </b-button>

      <b-modal id="main-form" ref="mainForm" centered :title="modeTitle" @hide="onHide">
        <form>
          <vue-form-generator
            ref="vfg"
            :model="selectedRow"
            :options="formOptions"
            :schema="schema"
            @validated="onValidated"
          />
        </form>
        <template #modal-footer>
          <ResendWelcomeEmail
            v-if="removingItem.text && mode === 'update'"
            @resend-welcome-email="resendWelcomeEmail"
          ></ResendWelcomeEmail>
          <b-button
            v-if="removingItem.text && mode === 'update'"
            data-test-id="RemoveUserButton"
            variant="outline-danger"
            :disabled="removingItem.waiting"
            @click="save($event, 'remove')"
          >
            <b-icon-trash></b-icon-trash> {{ removingItem.text }}
          </b-button>
          <b-button
            v-if="addExistingHeaderText && mode === 'existing'"
            data-test-id="AddExistingUserButton"
            class="mr-auto"
            variant="outline-primary"
            @click="save($event, 'existing')"
          >
            {{ addExistingItemText }}
          </b-button>
          <b-button variant="outline-secondary" @click="close()">
            Cancel
          </b-button>
          <b-button v-if="showSaveButton" :disabled="!canSubmit" variant="outline-success" @click="save($event, mode)">
            Save
          </b-button>
        </template>
      </b-modal>
    </div>
    <div class="clearfix"></div>
    <div ref="tableContainer">
      <ag-grid-vue
        ref="listFormAgGrid"
        :style="gridStyle"
        class="ag-theme-balham"
        :column-defs="columnsDescription"
        :default-col-def="defaultColumnDef"
        :suppress-no-rows-overlay="true"
        :row-data="data"
        row-selection="single"
        @row-clicked="onRowClicked"
        @grid-ready="onGridReady"
        @first-data-rendered="updateRowCount"
        @filter-changed="updateRowCount"
        @sort-changed="updateRowCount"
        @row-data-changed="updateRowCount"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { AgGridVue } from "ag-grid-vue";
import { BIconArrowClockwise, BIconDownload } from "bootstrap-vue";
import Vue, { nextTick } from "vue";
import VueFormGenerator from "vue-form-generator";
import { mapGetters } from "vuex";
import Spinner from "@/App/components/Spinner.vue";
import ResendWelcomeEmail from "@/Users/components/ResendWelcomeEmail.vue";

function processData(data: any): any {
  return JSON.parse(JSON.stringify(data));
}

export default Vue.extend({
  name: "ListForm",
  components: {
    AgGridVue,
    Spinner,
    BIconArrowClockwise,
    BIconDownload,
    ResendWelcomeEmail,
    "vue-form-generator": VueFormGenerator.component
  },
  props: {
    id: {
      type: String,
      default: ""
    },
    data: {
      type: Array,
      default: (): any => []
    },
    defaultColumnDef: {
      type: Object,
      default: (): any => ({})
    },
    listTitle: {
      type: String,
      default: ""
    },
    singleName: {
      type: String,
      default: ""
    },
    pluralName: {
      type: String,
      default: ""
    },
    columnsDescription: {
      type: Array,
      default: (): any => []
    },
    createItemFormDescription: {
      type: Array,
      default: (): any => []
    },
    updateFormGroups: {
      type: Array,
      default: (): any => []
    },
    altFormGroups: {
      type: Array,
      default: (): any => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    showModalButton: {
      type: String,
      default: "true"
    },
    addNewItemText: {
      type: String,
      default: ""
    },
    viewEditItemText: {
      type: String,
      default: "View/Edit Selected"
    },
    addExistingHeaderText: {
      type: String,
      default: ""
    },
    addExistingItemText: {
      type: String,
      default: ""
    },
    removeItemText: {
      type: String,
      default: ""
    },
    removeItemConfirmationText: {
      type: String,
      default: ""
    },
    tableStyle: {
      type: Object,
      default: (): any => ({})
    },
    canUpdate: {
      type: Boolean,
      default: true
    },
    refreshType: {
      type: String,
      default: ""
    },
    showCsv: {
      type: Boolean,
      default: false
    },
    countMessage: {
      type: String,
      default: ""
    },
    defaultSelectedRow: {
      default: function() {
        return {};
      },
      type: Function
    }
  },
  data(): any {
    return {
      rowData: [],
      gridApi: null,
      columnApi: null,
      selectedRowIndex: -1,
      // @ts-ignore
      selectedRow: this.defaultSelectedRow(),
      formShow: false,
      canSubmit: false,
      model: {},
      schema: {
        groups: this.createItemFormDescription
      },
      formOptions: {
        validateAfterChanged: true,
        validateAsync: true
      },
      gridStyle: (this as any).setGridStyle(),
      mode: "create",
      removingItem: {
        final: false,
        text: this.removeItemText,
        waiting: false
      },
      filteredRowCount: 0
    };
  },
  computed: {
    ...mapGetters("Error", ["isError"]),
    modeTitle(): any {
      if (this.mode === "update") {
        return `${this.viewEditItemText} ${this.singleName}`;
      } else if (this.mode === "create") {
        return this.addNewItemText;
      } else {
        return this.addExistingHeaderText;
      }
    },
    showSaveButton(): any {
      return this.mode === "create" || (this.mode === "update" && this.canUpdate);
    },
    filterId(): string {
      return `${this.id}Filters`;
    }
  },
  watch: {
    data(): any {
      this.setGridStyle();
      nextTick().then(() => (this.filteredRowCount = this.gridApi?.getModel?.().rootNode?.childrenAfterFilter?.length));
    }
  },
  beforeDestroy(): any {
    this.gridApi.eventService.removeEventListener("filterChanged", this.saveFilters);
    window.removeEventListener("resize", this.setGridStyle);
  },
  mounted(): any {
    window.addEventListener("resize", this.setGridStyle);
    this.setGridStyle();
  },
  methods: {
    updateRowCount(): any {
      if (this.gridApi) {
        this.filteredRowCount = this.gridApi.getModel().rootNode.childrenAfterFilter.length;
      }
    },
    showModal(): any {
      if (this.selectedRow === -1) {
        this.selectedRow({ data: this.defaultSelectedRow() });
      }
      this.$refs.mainForm.show();
    },
    hideModal(): any {
      if (this.showModalButton === "true") {
        this.$refs.mainForm.hide();
      }
    },
    setGridStyle(): any {
      if (this.$refs.tableContainer) {
        const tableBottomMargin = 20;
        const maxHeight =
          window.innerHeight - tableBottomMargin - this.$refs.tableContainer.getBoundingClientRect().top;
        const dataHeight = maxHeight;
        const tableHeight = Math.min(maxHeight, dataHeight);

        this.$nextTick(() => {
          this.loadFilters();
          this.gridStyle = {
            height: `${tableHeight}px`,
            ...this.tableStyle
          };
          const allColumnIds = this.columnApi?.getAllColumns().map(column => column.getId()) ?? [];
          this.columnApi?.autoSizeColumns(allColumnIds);
        });
      }
    },
    loadFilters(): void {
      if (this.id) {
        const listFormFilters = localStorage.getItem(this.filterId);

        if (listFormFilters) {
          this.gridApi?.setFilterModel(JSON.parse(listFormFilters));
        }
      }
    },
    saveFilters(): void {
      if (this.id) {
        const listFormFilters = this.gridApi?.getFilterModel();

        if (listFormFilters && Object.keys(listFormFilters).length) {
          localStorage.setItem(this.filterId, JSON.stringify(listFormFilters));
        } else {
          localStorage.removeItem(this.filterId);
        }
      }
    },
    onValidated(isValid: any): any {
      if (this.mode === "existing") {
        this.canSubmit = true;
      } else {
        this.canSubmit = isValid;
      }
    },
    onGridReady(parameters: any): any {
      this.gridApi = parameters.api;
      this.columnApi = parameters.columnApi;
      this.gridApi.sizeColumnsToFit();
      this.loadFilters();
      this.gridApi.eventService.addEventListener("filterChanged", this.saveFilters);
    },
    useAltFormGroups(data: any): any {
      this.selectedRow = data;
      this.schema = { groups: this.altFormGroups };
      this.mode = "existing";
      this.canSubmit = true;
      this.showModal();
    },
    selectRow(event: any): any {
      this.selectedRowIndex = event.rowIndex;
      this.selectedRow = { ...event.data };
      this.schema = { groups: this.updateFormGroups };
      this.$emit("selectedRowChange", this.selectedRow);
    },
    unselectRow(index: any): any {
      if (this.gridApi && index) {
        this.gridApi.getRowNode(index).setSelected(false);
      }
      this.selectedRowIndex = -1;
      this.selectedRow = this.defaultSelectedRow();
      this.mode = "create";
      this.formShow = false;
      this.schema = { groups: this.createItemFormDescription };
      this.$emit("selectedRowChange", {});
    },
    onRowClicked(event: any): any {
      const isSelecting = event.rowIndex !== this.selectedRowIndex;

      isSelecting ? this.selectRow(event) : this.unselectRow(event.rowIndex);

      this.mode = isSelecting ? "update" : "create";

      this.$emit("row-clicked", event);
    },
    async save(event: any, mode: any): Promise<any> {
      if (mode === "create") {
        return await this.baseSave(event, this.insertRow);
      } else if (mode === "update") {
        return await this.baseSave(event, this.updateRow);
      } else if (mode === "remove") {
        return this.saveRemove(event);
      } else if (mode === "existing") {
        return await this.baseSave(event, this.addExistingUser);
      }
    },
    async baseSave(event: any, functionToCall: any) {
      event.preventDefault();
      const errors = await this.$refs.vfg.validate();
      if (errors?.length > 0) {
        return;
      }
      functionToCall(processData(this.selectedRow));
      this.close();
    },
    saveRemove(event: any): any {
      event.preventDefault();
      if (this.removingItem.final === false) {
        this.removingItem.final = true;
        this.removingItem.waiting = true;
        this.removingItem.text = "Wait to confirm...";

        window.setTimeout(() => {
          this.removingItem.text = this.removeItemConfirmationText;
          this.removingItem.waiting = false;
        }, 1000);
      } else {
        this.removeRow(processData(this.selectedRow));
        this.close();
      }
    },
    close(): void {
      this.unselectRow();
      this.hideModal();
    },
    insertRow(data: any): any {
      this.$emit("insert-row", data);
    },
    updateRow(data: any): any {
      this.$emit("update-row", data.id, data);
    },
    removeRow(data: any): any {
      this.$emit("remove-row", data.id, data);
    },
    addExistingUser(data: any): any {
      this.$emit("add-exisitng-user", data.id, data);
    },
    resendWelcomeEmail(): any {
      this.$emit("resend-welcome-email", this.selectedRow.username);
    },
    onBtExport(): void {
      const parameters = {
        skipHeader: false,
        fileName: "twiagelist.csv",
        columnSeparator: ","
      };
      this.gridApi.exportDataAsCsv(parameters);
    },
    onHide(): any {
      this.unselectRow();
      this.removingItem.text = this.removeItemText;
      this.removingItem.waiting = false;
      this.removingItem.final = false;
    },
    onRefresh(): any {
      if (this.refreshType) {
        this.$emit("list-refresh", this.refreshType);
      }
    }
  }
});
</script>

<style src="ag-grid-community/dist/styles/ag-grid.min.css" />
<style src="ag-grid-community/dist/styles/ag-theme-balham.min.css" />

<style lang="css">
.ag-header-cell-text {
  font-weight: bold;
  color: black;
}
.modal-footer {
  justify-content: space-between !important;
}
.ag-body-viewport {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.ag-body-viewport::-webkit-scrollbar {
  width: 0;
  height: 0;
}
</style>

<style lang="css" scoped>
.list-form-total-line {
  float: left;
  font-weight: bold;
  font-size: 95%;
  color: black;
}

.list-form-refresh {
  float: right;
}
.top-right {
  float: right;
  margin-top: -50px;
}
@media only screen and (max-width: 768px) {
  .top-right {
    display: none;
  }
}
</style>
